import type { JobFamily, SearchCounts, SearchPosting, WorkSetting } from "@/lib/api/catalogue";
import { JOB_FAMILIES, SENIORITIES } from "@/lib/api/catalogue";
import type { EnvoySettings } from "@/lib/api/envoy";
import { formatCount } from "@/lib/format";
import { SENIORITY_LABEL } from "./labels";

/**
 * The filter bar, read back: its chips in order, and the counts beside
 * them. Pure.
 */

/** How many families the bar shows before "N more". */
export const FAMILIES_SHOWN = 4;

/**
 * The families in the order the bar offers them: the ones picked, then
 * the commonest among the postings read so far, then the catalogue's own
 * order. The catalogue counts no families, so "commonest" is of what is
 * loaded.
 */
export function familyOrder(
    postings: readonly SearchPosting[],
    picked: readonly JobFamily[],
): JobFamily[] {
    const tally = postings.reduce<Partial<Record<JobFamily, number>>>((totals, posting) => {
        const family = posting.reading?.family;
        return family ? { ...totals, [family]: (totals[family] ?? 0) + 1 } : totals;
    }, {});
    const rank = (family: JobFamily) =>
        (picked.includes(family) ? 1_000_000 : 0) + (tally[family] ?? 0);
    return [...JOB_FAMILIES].sort(
        (left, right) =>
            rank(right) - rank(left) || JOB_FAMILIES.indexOf(left) - JOB_FAMILIES.indexOf(right),
    );
}

/** Every level, each with whether the envoy looks for it. */
export function seniorityChips(settings: EnvoySettings | null) {
    return SENIORITIES.map((level) => ({
        level,
        label: SENIORITY_LABEL[level],
        on: settings?.seniority.includes(level) ?? false,
    }));
}

/** "6 shown · 38 match". */
export function shownLine(shown: number, total: number | undefined): string {
    return total === undefined
        ? `${formatCount(shown)} shown`
        : `${formatCount(shown)} shown · ${formatCount(total)} match`;
}

/**
 * How many the search keeps only because a board left a filter
 * unstated or the model has not read the posting: the count with
 * `unstated` less the count without it.
 */
export function unreadKept(
    withUnread: SearchCounts | undefined,
    withoutUnread: SearchCounts | undefined,
): number | undefined {
    if (!withUnread || !withoutUnread) return undefined;
    return Math.max(0, withUnread.total - withoutUnread.total);
}

/** The work setting's count from a search's counts. */
export function settingCount(counts: SearchCounts | undefined, setting: WorkSetting) {
    if (!counts) return undefined;
    const by: Record<WorkSetting, number> = {
        REMOTE: counts.work_settings.remote,
        HYBRID: counts.work_settings.hybrid,
        ON_SITE: counts.work_settings.on_site,
    };
    return by[setting];
}
